import { type ReactNode } from 'react';

export type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'critical' | 'info' | 'purple' | string;

export interface BadgeProps {
  title?: string;
  children: ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'purple';
  tone?: BadgeTone;
  size?: 'sm' | 'md';
  className?: string;
  onClick?: () => void;
}

const TONE_TO_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'danger' | 'info' | 'purple'> = {
  neutral: 'default',
  default: 'default',
  primary: 'info',
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  critical: 'danger',
  purple: 'purple',
};

const VARIANT_CLASSES = {
  default: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  danger:  'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  info:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  purple:  'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

const SIZE_CLASSES = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2 py-0.5 text-xs',
};

export function Badge({ children, variant, tone, size = 'md', className = '', onClick }: BadgeProps) {
  const effectiveVariant = variant || (tone ? TONE_TO_VARIANT[tone] || 'default' : 'default');
  return (
    <span
      onClick={onClick}
      className={`inline-flex items-center font-medium rounded-full ${VARIANT_CLASSES[effectiveVariant]} ${SIZE_CLASSES[size]} ${onClick ? 'cursor-pointer hover:opacity-85' : ''} ${className}`}
    >
      {children}
    </span>
  );
}
